import React, { useCallback, useEffect, useState } from 'react';
import client, { endpoints, errText } from '../../api/client';
import { dt } from '../../lib/format';

// Cities covered, and the waitlist that says which one to open next. The waitlist
// ranking is the point: expansion becomes a number rather than a hunch.
export default function Coverage() {
  const [areas, setAreas] = useState([]);
  const [waitlist, setWaitlist] = useState({ items: [], byCity: [] });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, w] = await Promise.all([client.get(endpoints.areas.all), client.get(endpoints.waitlist.list)]);
      setAreas(a.data.items || []);
      setWaitlist(w.data || { items: [], byCity: [] });
    } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = (fn) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  return (
    <section className="section">
      <div className="section-head"><h1>Coverage</h1></div>
      {err && <p className="alert alert-err">{err}</p>}

      <div className="card">
        <h2>Cities you cover</h2>
        <table className="table table-tight">
          <thead><tr><th>City</th><th>Region</th><th>Teams</th><th>Capacity/slot</th><th>Bookings</th><th>Live</th></tr></thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a._id}>
                <td><strong>{a.city}</strong><div className="muted small">{a.slug}</div></td>
                <td>{a.region || '—'}</td>
                <td>{a.teams}</td>
                <td className={a.capacity ? '' : 'due'}>{a.capacity || 'no team'}</td>
                <td>{a.bookings}</td>
                <td>
                  <label className="check">
                    <input type="checkbox" checked={!!a.active} disabled={busy}
                           onChange={() => act(() => client.put(endpoints.areas.update(a._id), { active: !a.active }))()} />
                    active
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form className="inline-form" onSubmit={(e) => {
          e.preventDefault();
          const f = e.target.elements;
          act(() => client.post(endpoints.areas.create, { city: f.city.value, region: f.region.value }))();
          e.target.reset();
        }}>
          <input name="city" placeholder="New city" required />
          <input name="region" placeholder="State" />
          <button className="btn-primary" disabled={busy}>Add city</button>
        </form>
        <p className="muted small">A city with bookings is paused rather than deleted, so its history stays readable.</p>
      </div>

      <div className="card">
        <h2>Where people are asking for you</h2>
        {waitlist.byCity.length === 0 && <p className="muted">Nobody on the waitlist yet.</p>}
        {waitlist.byCity.length > 0 && (
          <table className="table table-tight">
            <thead><tr><th>City</th><th>Requests</th><th>Latest</th></tr></thead>
            <tbody>
              {waitlist.byCity.map((c) => (
                <tr key={c.cityKey}><td>{c.city}</td><td><strong>{c.requests}</strong></td><td>{dt(c.latest)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Waitlist</h2>
        {waitlist.items.length === 0 && <p className="muted">Empty.</p>}
        {waitlist.items.length > 0 && (
          <table className="table table-tight">
            <thead><tr><th>Joined</th><th>City</th><th>Person</th><th>Contact</th><th>Told?</th></tr></thead>
            <tbody>
              {waitlist.items.map((w) => (
                <tr key={w._id}>
                  <td>{dt(w.createdAt)}</td>
                  <td>{w.city}</td>
                  <td>{w.name || <span className="muted">—</span>}</td>
                  <td>{w.email}{w.phone ? <div className="muted small">{w.phone}</div> : null}</td>
                  <td>
                    <label className="check">
                      <input type="checkbox" checked={!!w.notified} disabled={busy}
                             onChange={() => act(() => client.put(endpoints.waitlist.notified(w._id), { notified: !w.notified }))()} />
                      notified
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
